'use client';
import React, { useState, useRef, useCallback } from 'react';
import PresetsPanel from '@/components/PresetsPanel';
import { usePresets } from '@/lib/usePresets';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const DURATION_PRESETS = [1, 2, 3, 5];

interface FadePresetSettings {
  enableFadeIn: boolean;
  enableFadeOut: boolean;
  fadeInDuration: number;
  fadeOutDuration: number;
  fadeInCustom: boolean;
  fadeOutCustom: boolean;
  fadeInCustomVal: number;
  fadeOutCustomVal: number;
  outputFormat: 'mp3' | 'wav';
}

export default function AudioFadeTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number } | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const [enableFadeIn, setEnableFadeIn] = useState(true);
  const [enableFadeOut, setEnableFadeOut] = useState(true);
  const [fadeInDuration, setFadeInDuration] = useState(2);
  const [fadeOutDuration, setFadeOutDuration] = useState(2);
  const [fadeInCustom, setFadeInCustom] = useState(false);
  const [fadeOutCustom, setFadeOutCustom] = useState(false);
  const [fadeInCustomVal, setFadeInCustomVal] = useState(2);
  const [fadeOutCustomVal, setFadeOutCustomVal] = useState(2);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { presets, savePreset, deletePreset } = usePresets<FadePresetSettings>('audio-fade');

  const getCurrentSettings = (): FadePresetSettings => ({
    enableFadeIn,
    enableFadeOut,
    fadeInDuration,
    fadeOutDuration,
    fadeInCustom,
    fadeOutCustom,
    fadeInCustomVal,
    fadeOutCustomVal,
    outputFormat,
  });

  const applyPresetSettings = (settings: FadePresetSettings) => {
    setEnableFadeIn(settings.enableFadeIn);
    setEnableFadeOut(settings.enableFadeOut);
    setFadeInDuration(settings.fadeInDuration);
    setFadeOutDuration(settings.fadeOutDuration);
    setFadeInCustom(settings.fadeInCustom);
    setFadeOutCustom(settings.fadeOutCustom);
    setFadeInCustomVal(settings.fadeInCustomVal);
    setFadeOutCustomVal(settings.fadeOutCustomVal);
    setOutputFormat(settings.outputFormat);
    setResultUrl(null);
  };

  const effectiveFadeIn = fadeInCustom ? fadeInCustomVal : fadeInDuration;
  const effectiveFadeOut = fadeOutCustom ? fadeOutCustomVal : fadeOutDuration;

  const loadFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const allowedExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'webm', 'flac'];
    if (!allowedExts.includes(ext)) {
      setError('Unsupported file type. Please upload MP3, WAV, OGG, M4A, AAC, WebM, or FLAC.');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError('File too large. Maximum supported size is 200 MB.');
      return;
    }
    setError(null);
    setResultUrl(null);
    setAudioDuration(null);
    const url = URL.createObjectURL(file);
    setAudioFile({ file, url, size: file.size });
  }, []);

  const handleAudioLoaded = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const dur = e.currentTarget.duration;
    if (isFinite(dur)) setAudioDuration(dur);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) loadFile(f);
  };

  const handleReset = () => {
    setAudioFile(null);
    setAudioDuration(null);
    setEnableFadeIn(true);
    setEnableFadeOut(true);
    setFadeInDuration(2);
    setFadeOutDuration(2);
    setFadeInCustom(false);
    setFadeOutCustom(false);
    setFadeInCustomVal(2);
    setFadeOutCustomVal(2);
    setOutputFormat('mp3');
    setResultUrl(null);
    setResultSize(0);
    setError(null);
    setProgressPct(0);
    setProgressLabel('');
  };

  // Validation
  const fadeInError = enableFadeIn && audioDuration != null && effectiveFadeIn > audioDuration
    ? `Fade in duration (${effectiveFadeIn}s) exceeds audio length (${formatDuration(audioDuration)}).`
    : null;
  const fadeOutError = enableFadeOut && audioDuration != null && effectiveFadeOut > audioDuration
    ? `Fade out duration (${effectiveFadeOut}s) exceeds audio length (${formatDuration(audioDuration)}).`
    : null;
  const hasValidationError = !!(fadeInError || fadeOutError);
  const nothingEnabled = !enableFadeIn && !enableFadeOut;

  const handleProcess = async () => {
    if (!audioFile || hasValidationError || nothingEnabled) return;
    setProcessing(true);
    setProgressPct(5);
    setProgressLabel('Loading ffmpeg…');
    setError(null);
    setResultUrl(null);

    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

      setProgressPct(15);
      setProgressLabel('Initialising ffmpeg…');

      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress }: { progress: number }) => {
        setProgressPct(Math.round(25 + progress * 65));
        setProgressLabel(`Processing… ${Math.round(progress * 100)}%`);
      });

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgressPct(22);
      setProgressLabel('Reading file…');

      const inputExt = audioFile.file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
      const inputName = `input.${inputExt}`;
      const outputName = `output.${outputFormat}`;

      await ffmpeg.writeFile(inputName, await fetchFile(audioFile.file));

      setProgressPct(25);
      setProgressLabel('Applying fade effects…');

      // Build afade filter string
      const filters: string[] = [];
      if (enableFadeIn) {
        filters.push(`afade=t=in:st=0:d=${effectiveFadeIn}`);
      }
      if (enableFadeOut && audioDuration != null) {
        const fadeOutStart = Math.max(0, audioDuration - effectiveFadeOut);
        filters.push(`afade=t=out:st=${fadeOutStart.toFixed(3)}:d=${effectiveFadeOut}`);
      }

      const filterStr = filters.join(',');

      const ffmpegArgs = ['-i', inputName];
      if (filterStr) {
        ffmpegArgs.push('-filter:a', filterStr);
      }
      if (outputFormat === 'mp3') {
        ffmpegArgs.push('-codec:a', 'libmp3lame', '-q:a', '2');
      } else {
        ffmpegArgs.push('-codec:a', 'pcm_s16le');
      }
      ffmpegArgs.push(outputName);

      await ffmpeg.exec(ffmpegArgs);

      setProgressPct(92);
      setProgressLabel('Exporting…');

      const data = await ffmpeg.readFile(outputName);
      const mimeType = outputFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav';
      const blob = new Blob([data], { type: mimeType });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgressPct(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Processing failed. Please try a different file or format.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !audioFile) return;
    const baseName = audioFile.file.name.replace(/\.[^.]+$/, '');
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${baseName}_faded.${outputFormat}`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {/* Error */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Upload Zone */}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🌅</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">MP3, WAV, OGG, M4A, AAC, WebM, FLAC · Max 200 MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,.aac,.webm,.flac"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* File info */}
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🌅</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)}{audioDuration != null ? ` · ${formatDuration(audioDuration)}` : ''}</p>
            </div>
            <button onClick={handleReset} className="text-gray-400 hover:text-red-500 p-1 transition-colors" title="Reset">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Original preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Original Audio</p>
            <audio
              src={audioFile.url}
              controls
              className="w-full h-10"
              onLoadedMetadata={handleAudioLoaded}
            />
            {audioDuration != null && (
              <p className="text-xs text-gray-400 mt-1.5">Duration: {formatDuration(audioDuration)}</p>
            )}
          </div>

          {/* Presets Panel */}
          <PresetsPanel
            presets={presets}
            onSave={(name) => savePreset(name, getCurrentSettings())}
            onLoad={applyPresetSettings}
            onDelete={deletePreset}
            accentColor="amber"
          />

          {/* Fade In */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Fade In</p>
                <p className="text-xs text-gray-400">Gradually increase volume at the start</p>
              </div>
              <button
                onClick={() => { setEnableFadeIn(!enableFadeIn); setResultUrl(null); }}
                className={`w-12 h-6 rounded-full transition-colors relative ${enableFadeIn ? 'bg-amber-500' : 'bg-gray-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${enableFadeIn ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {enableFadeIn && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">Fade In Duration</p>
                <div className="flex flex-wrap gap-2">
                  {DURATION_PRESETS.map((d) => (
                    <button
                      key={d}
                      onClick={() => { setFadeInDuration(d); setFadeInCustom(false); setResultUrl(null); }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!fadeInCustom && fadeInDuration === d ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-700'}`}
                    >
                      {d}s
                    </button>
                  ))}
                  <button
                    onClick={() => { setFadeInCustom(true); setResultUrl(null); }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${fadeInCustom ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-700'}`}
                  >
                    Custom
                  </button>
                </div>
                {fadeInCustom && (
                  <div className="flex items-center gap-3 mt-2">
                    <input
                      type="number"
                      min={0.1}
                      max={audioDuration ?? 300}
                      step={0.1}
                      value={fadeInCustomVal}
                      onChange={(e) => { setFadeInCustomVal(Number(e.target.value)); setResultUrl(null); }}
                      className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                    <span className="text-sm text-gray-500">seconds</span>
                  </div>
                )}
                {fadeInError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.007v.008H12v-.008z" /></svg>
                    {fadeInError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Fade Out */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-800">Fade Out</p>
                <p className="text-xs text-gray-400">Gradually decrease volume at the end</p>
              </div>
              <button
                onClick={() => { setEnableFadeOut(!enableFadeOut); setResultUrl(null); }}
                className={`w-12 h-6 rounded-full transition-colors relative ${enableFadeOut ? 'bg-amber-500' : 'bg-gray-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${enableFadeOut ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {enableFadeOut && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium">Fade Out Duration</p>
                <div className="flex flex-wrap gap-2">
                  {DURATION_PRESETS.map((d) => (
                    <button
                      key={d}
                      onClick={() => { setFadeOutDuration(d); setFadeOutCustom(false); setResultUrl(null); }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!fadeOutCustom && fadeOutDuration === d ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-700'}`}
                    >
                      {d}s
                    </button>
                  ))}
                  <button
                    onClick={() => { setFadeOutCustom(true); setResultUrl(null); }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${fadeOutCustom ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-amber-50 hover:text-amber-700'}`}
                  >
                    Custom
                  </button>
                </div>
                {fadeOutCustom && (
                  <div className="flex items-center gap-3 mt-2">
                    <input
                      type="number"
                      min={0.1}
                      max={audioDuration ?? 300}
                      step={0.1}
                      value={fadeOutCustomVal}
                      onChange={(e) => { setFadeOutCustomVal(Number(e.target.value)); setResultUrl(null); }}
                      className="w-24 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                    <span className="text-sm text-gray-500">seconds</span>
                  </div>
                )}
                {fadeOutError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.007v.008H12v-.008z" /></svg>
                    {fadeOutError}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Output Format */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Output Format</p>
            <div className="flex gap-3">
              {(['mp3', 'wav'] as const).map((fmt) => (
                <button
                  key={fmt}
                  onClick={() => { setOutputFormat(fmt); setResultUrl(null); }}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${outputFormat === fmt ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300'}`}
                >
                  {fmt.toUpperCase()}
                  <span className="block text-xs font-normal mt-0.5 opacity-70">{fmt === 'mp3' ? 'Smaller size' : 'Lossless'}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nothing enabled warning */}
          {nothingEnabled && (
            <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-100 rounded-xl p-3">
              <span className="text-yellow-500 text-lg">⚠️</span>
              <p className="text-yellow-700 text-sm">Enable at least one fade effect to process.</p>
            </div>
          )}

          {/* Progress */}
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>{progressLabel}</span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}

          {/* Result */}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-semibold text-green-800 text-sm">Fade applied! · {formatBytes(resultSize)}</p>
              </div>
              <p className="text-xs text-gray-500 font-medium">Processed Audio</p>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button
                onClick={handleDownload}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                ⬇️ Download {outputFormat.toUpperCase()}
              </button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-none px-5 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors text-sm"
            >
              ↺ Reset
            </button>
            {!resultUrl && (
              <button
                onClick={handleProcess}
                disabled={processing || hasValidationError || nothingEnabled}
                className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
              >
                {processing ? 'Processing…' : '✨ Apply Fade Effects'}
              </button>
            )}
            {resultUrl && (
              <button
                onClick={() => { setResultUrl(null); setResultSize(0); }}
                className="flex-1 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-sm"
              >
                ✨ Apply Again
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
